import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { FieldDefinition } from '@lens/shared';
import { parseWidthFromSrc } from './resizableImage.ts';
import { remarkColorTags } from './textColor.ts';
import { Chip } from '../components/Chip.tsx';
import type { StatusCategoryKey } from '../theme/tokens.ts';
import './markdown-field.css';

/** Категории статусного поля, известные примитиву Chip. */
const STATUS_CATEGORIES: StatusCategoryKey[] = ['backlog', 'active', 'done', 'cancelled'];
function asStatusCategory(category: unknown): StatusCategoryKey | undefined {
  return STATUS_CATEGORIES.includes(category as StatusCategoryKey)
    ? (category as StatusCategoryKey)
    : undefined;
}

/**
 * Картинка в режиме просмотра. Ширина закодирована в URL как `?w=300`
 * (см. resizableImage.ts) — вытаскиваем её и применяем как inline-стиль.
 */
const markdownComponents: Components = {
  // Ссылка внутри markdown-поля: открывается в новой вкладке и НЕ доходит до
  // обработчиков поля. Без stopPropagation клик всплывал до контейнера
  // значения (карточка записи, ячейка грида) и переводил поле в правку —
  // переход по ссылке не происходил.
  a: ({ href, children }) => (
    <a
      href={typeof href === 'string' ? href : undefined}
      target="_blank"
      rel="noreferrer noopener"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  ),
  img: ({ src, alt }) => {
    const { src: cleanSrc, width } = parseWidthFromSrc(typeof src === 'string' ? src : '');
    return <img src={cleanSrc} alt={alt ?? ''} style={width ? { width } : undefined} />;
  },
};

/** remarkColorTags — цвет текста/выделение и подчёркивание (см. textColor.ts). */
const MARKDOWN_PLUGINS = [remarkGfm, remarkColorTags];

/**
 * Мемоизированная ячейка markdown — рендерит ReactMarkdown один раз
 * и переиспользует DOM при ресайзе колонок / неизменном значении.
 */
export const MarkdownCell = React.memo(function MarkdownCell({
  value,
  variant = 'full',
}: {
  value: string;
  /** 'cell' — компактное превью в ячейке грида (clamp до 2 строк). */
  variant?: 'cell' | 'full';
}) {
  return (
    <div className={variant === 'cell' ? 'markdown-field markdown-field--cell' : 'markdown-field'}>
      <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={markdownComponents}>{value}</ReactMarkdown>
    </div>
  );
}, (prev, next) => prev.value === next.value && prev.variant === next.variant);

/**
 * Безопасный href для url-поля. Голый домен («example.com») дополняем до
 * `https://…`, явные http/https/mailto пропускаем как есть, любую другую
 * схему (javascript:, data:, …) не линкуем вовсе — null.
 */
export function safeUrlHref(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return trimmed;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
  return `https://${trimmed}`;
}

export function formatDate(isoDate: string): string {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Текстовый аналог renderFieldValue: возвращает человекочитаемую строку
 * (label справочника/опции, Да/Нет, форматированная дата) вместо JSX.
 * Используется для экспорта в Excel — «что вижу на экране, то и выгружаю».
 */
export function fieldValueToText(
  value: unknown,
  field: FieldDefinition,
  resolvedRefs?: Record<string, string>,
): string {
  if (value === null || value === undefined) return '';
  if (field.fieldType === 'boolean') return value ? 'Да' : 'Нет';
  if (field.fieldType === 'date') return formatDate(String(value));
  if (field.fieldType === 'select') {
    const config = field.config as { options?: Array<{ value: string; label: string }> };
    const opt = config.options?.find((o) => o.value === value);
    return opt ? (opt.label || opt.value) : String(value);
  }
  if (field.fieldType === 'multi_select') {
    const config = field.config as { options?: Array<{ value: string; label: string }> };
    if (Array.isArray(value)) {
      return value.map((v: string) => config.options?.find((o) => o.value === v)?.label ?? v).join(', ');
    }
    return String(value);
  }
  if (field.fieldType === 'reference' || field.fieldType === 'user') {
    if (typeof value === 'string') return resolvedRefs?.[value] ?? value;
    if (Array.isArray(value)) return value.map((v: string) => resolvedRefs?.[v] ?? v).join(', ');
    return String(value);
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * Универсальный рендер значения поля.
 * Используется в таблице, канбане и drawer'е.
 */
export function renderFieldValue(
  value: unknown,
  field: FieldDefinition,
  resolvedRefs?: Record<string, string>,
  /**
   * 'full' (по умолчанию) — карточка/канбан/drawer, без ограничения высоты.
   * 'cell' — ячейка грида: markdown/длинный текст клампится до 2 строк, чтобы
   * не взрывать высоту строки.
   */
  variant: 'cell' | 'full' = 'full',
): React.ReactNode {
  if (value === null || value === undefined) return <span style={{ color: 'var(--color-text-secondary)' }}>—</span>;

  if (field.fieldType === 'computed') {
    return <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>{String(value)}</span>;
  }
  if (field.fieldType === 'markdown') {
    return <MarkdownCell value={String(value)} variant={variant} />;
  }
  // Длинный однострочный/многострочный текст в ячейке грида — клампим до 2 строк.
  if (variant === 'cell' && (field.fieldType === 'textarea' || field.fieldType === 'text')) {
    return <span className="markdown-field--cell">{String(value)}</span>;
  }
  // URL — кликабельная ссылка в новой вкладке. stopPropagation на click/focus,
  // чтобы клик по ссылке не запускал inline-редактор ячейки и не открывал drawer.
  if (field.fieldType === 'url') {
    const raw = String(value);
    const href = safeUrlHref(raw);
    const ellipsis: React.CSSProperties = {
      display: 'inline-block',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      verticalAlign: 'bottom',
    };
    if (!href) return <span style={ellipsis}>{raw}</span>;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={raw}
        style={ellipsis}
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
      >
        {raw}
      </a>
    );
  }
  if (field.fieldType === 'boolean') {
    return value ? 'Да' : 'Нет';
  }
  if (field.fieldType === 'date') {
    return formatDate(String(value));
  }
  if (field.fieldType === 'select') {
    const config = field.config as {
      hasCategories?: boolean;
      options?: Array<{ value: string; label: string; color?: string; category?: string }>;
    };
    const opt = config.options?.find((o) => o.value === value);
    if (!opt) return String(value);
    const category = asStatusCategory(opt.category);
    if (config.hasCategories === true && category) {
      return <Chip tone="status" category={category} label={opt.label || opt.value} />;
    }
    return <Chip tone="select" color={opt.color} label={opt.label || opt.value} />;
  }
  if (field.fieldType === 'multi_select') {
    const config = field.config as { options?: Array<{ value: string; label: string; color?: string }> };
    if (Array.isArray(value)) {
      return (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, maxWidth: '100%' }}>
          {value.map((v: string) => {
            const opt = config.options?.find((o) => o.value === v);
            return <Chip key={v} tone="select" color={opt?.color} label={opt?.label ?? v} />;
          })}
        </span>
      );
    }
  }
  if (field.fieldType === 'reference' || field.fieldType === 'user') {
    const labelOf = (v: string) => resolvedRefs?.[v] ?? v;
    if (typeof value === 'string') {
      return <Chip tone="reference" label={labelOf(value)} />;
    }
    if (Array.isArray(value)) {
      return (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, maxWidth: '100%' }}>
          {value.map((v: string) => (
            <Chip key={v} tone="reference" label={labelOf(v)} />
          ))}
        </span>
      );
    }
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return String(value);
}
